<template>
  <div class="notification-rules">
    <!-- Skeleton Loader -->
    <template v-if="loading">
      <div v-for="n in skeletonCount" :key="`skeleton-${n}`" class="border mb-2 bg-light"
        :style="{ borderColor: '#c6c6c6' }">
        <div class="p-2" style="min-height:62px;">
          <div class="d-flex align-items-start gap-3">
            <!-- Action skeleton -->
            <div style="width:200px;">
              <div class="skeleton skeleton-text" style="height: 40px; border-radius: 4px;"></div>
            </div>
            <!-- Content skeleton -->
            <div class="flex-fill">
              <div class="skeleton skeleton-text mb-2" style="height: 16px; width: 70%;"></div>
              <div class="d-flex gap-2 mt-2">
                <div class="skeleton skeleton-text" style="height: 30px; width: 100px;"></div>
                <div class="skeleton skeleton-text" style="height: 30px; width: 150px;"></div>
              </div>
            </div>
            <!-- Button skeleton -->
            <div class="d-flex flex-column align-items-end">
              <div class="skeleton skeleton-button" style="height: 32px; width: 100px; border-radius: 4px;"></div>
              <div class="skeleton skeleton-text mt-2" style="height: 14px; width: 30px;"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-2">
        <div class="skeleton skeleton-button" style="height: 32px; width: 80px; border-radius: 4px;"></div>
      </div>
    </template>

    <!-- Actual Content -->
    <template v-else>
      <div v-for="(rule, idx) in rulesState" :key="rule.id" class="rule-card mb-3"
        :class="getRuleCardClass(rule.action)">
        <!-- Rule header bar -->
        <div class="rule-card-header">
          <span class="rule-number-badge" :class="getRuleNumberClass(rule.action)">Rule #{{ startRuleNumberFromZero ? idx : idx + 1 }}</span>
          <button class="btn-rule-info" @click.stop="$emit('info', rule.id)" title="Learn more about this rule">
            <i class="material-icons">info_outline</i>
          </button>
        </div>
        <div class="rule-card-body">
          <div class="d-flex align-items-start gap-3">
            <div v-if="rule.action !== '' && !rule.actionInlineDescription" style="width:200px;">
              <div :class="getActionClass(rule.id, rule.action)">
                <MaterialSelect :id="`action-${rule.id}`" label="Action" :options="rule.actionOptions || actionOptions" v-model="rule.action" />
              </div>
            </div>
            <div class="flex-fill">
              <div v-if="rule.inlineDescription" class="small rule-desc rule-desc-inline">
                <span>{{ rule.descriptionPrefix }}</span>
                <MaterialSpinnerInput
                  :id="rule.inputFields[0].spinnerId || `field-${rule.id}-0`"
                  :modelValue="rule.inputFields[0].modelValue || 0"
                  :min="rule.inputFields[0].min"
                  :max="rule.inputFields[0].max"
                  :onSpinnerClick="(id, min, max) => check_spinner(id, min, max)"
                  @update:modelValue="(value) => { rule.inputFields[0].modelValue = value; }"
                />
                <span>{{ rule.descriptionMiddle }}</span>
                <MaterialSpinnerInput
                  :id="rule.inputFields[1].spinnerId || `field-${rule.id}-1`"
                  :modelValue="rule.inputFields[1].modelValue || 0"
                  :min="rule.inputFields[1].min"
                  :max="rule.inputFields[1].max"
                  :onSpinnerClick="(id, min, max) => check_spinner(id, min, max)"
                  @update:modelValue="(value) => { rule.inputFields[1].modelValue = value; }"
                />
                <span>{{ rule.descriptionSuffix }}</span>
              </div>
              <div v-else-if="rule.actionInlineDescription" class="small rule-desc rule-desc-inline">
                <span class="rule-action-select" :class="getActionClass(rule.id, rule.action)">
                  <MaterialSelect
                    :id="`action-${rule.id}`"
                    :aria-label="`Action for rule ${rule.id}`"
                    :options="rule.actionOptions || actionOptions"
                    v-model="rule.action"
                    :style="{ width: '130px', height: '30px' }"
                  />
                </span>
                <span>{{ rule.descriptionPrefix }}</span>
                <span>{{ rule.descriptionSuffix }}</span>
              </div>
              <div v-else class="small rule-desc" v-html="rule.descriptionHtml"></div>
              <!-- Action type subtitle -->
              <div v-if="rule.action === 'never'" class="rule-action-hint rule-hint-never">
                <i class="material-icons" style="font-size:14px;">block</i>
                <span>Hard rule — will <strong>exclude</strong> this person if the condition is met</span>
              </div>
              <div v-else-if="rule.action === 'when-possible'" class="rule-action-hint rule-hint-whenpossible">
                <i class="material-icons" style="font-size:14px;">tune</i>
                <span>Soft preference — <strong>adjusts probability</strong> but will not exclude the person</span>
              </div>
              <div v-if="!rule.inlineDescription" class="mt-2 d-flex flex-wrap align-items-center gap-2">
                <template v-for="(field, fi) in rule.inputFields" :key="`input-${rule.id}-${field.key}-${fi}`">
                  <div class="d-flex gap-2 align-items-center">
                    <span class="small text-muted" v-if="field.label">{{ field.label }}</span>

                    <span style="display: flex;">
                      <MaterialSelect v-if="field.type == 'select'" :id="`field-${rule.id}-${fi}`"
                        :options="field.options" v-model="field.modelValue" :style="{ width: '200px', height: '30px', margin: '0px 2px' }" />

                      <MaterialMultiSelect v-else-if="field.type === 'multi'" :id="`field-${rule.id}-${fi}`" 
                        :key="`multi-${rule.id}-${field.key}-${fi}`" label="" size="lg"
                        :items="field.options" v-model="field.modelValue" itemTrackBy="value" itemLabel="label"
                        placeholder="Select..." :style="{ width: '200px', height: '30px', margin: '0px 2px' }" :allowSelectAll=false
                        :showSelectedOptions="false" />

                      <!-- Spinner input for readonly numeric fields -->
                      <MaterialSpinnerInput 
                        v-else-if="field.useSpinner && field.type === 'number'"
                        :id="field.spinnerId || `field-${rule.id}-${fi}`"
                        :modelValue="field.modelValue || 0"
                        :min="field.min"
                        :max="field.max"
                        :onSpinnerClick="(id, min, max) => check_spinner(id, min, max)"
                        @update:modelValue="(value) => { field.modelValue = value; }"
                      />

                      <MaterialInput v-else :id="`field-${rule.id}-${fi}`" :type="field.type || 'text'"
                        v-model="field.modelValue" :placeholder="field.placeholder"
                        :min="field.min" :max="field.max"
                        :style="{ width: '100px', height: '34px', margin: '0px 2px' }" />

                    </span>

                    <span class="small text-muted" v-if="field.suffix">{{ field.suffix }}</span>
                  </div>
                </template>
              </div>
              <div v-if="rule.showSearch">
                <div class="d-flex align-items-end gap-2">
                  <div style="width: 50%">
                    <MaterialInput id="rule-search" placeholder="Search rules..." v-model="searchQuery"
                      :style="{ width: '100px' }" />
                  </div>
                  <div>
                    <MaterialButton variant="outlined" color="success" size="sm" @click="saveAll" :disabled="isAllSaving">
                      <span v-if="isAllSaving" class="spinner-border spinner-border-sm me-1" role="status"
                        aria-hidden="true"></span>
                      <span>{{ isAllSaving ? 'Saving...' : 'Search' }}</span>
                    </MaterialButton>
                  </div>
                </div>
                <div class="mt-4">
                  <MaterialSelectedBadge v-for="(item, i) in filteredRules" :key="i" :label="item" closable
                    @remove="removeFromResults(item)" color="danger" />
                </div>
              </div>
            </div>
            <div class="d-flex flex-column align-items-center">
              <MaterialButton v-if="!(startRuleNumberFromZero && idx === 0)" size="sm" color="info" @click="saveRule(rule)" :disabled="isRuleSaving(rule.id)">
                <span v-if="isRuleSaving(rule.id)" class="spinner-border spinner-border-sm me-1" role="status"
                  aria-hidden="true"></span>
                <i v-else class="material-icons text-sm me-1">save</i>
                <span>{{ isRuleSaving(rule.id) ? 'Saving...' : 'Save rule' }}</span>
              </MaterialButton>
            </div>
          </div>
        </div>
      </div>
      <!-- <div class="d-flex justify-content-end mt-2" v-if="saveAllAllowed">
        <MaterialButton variant="outlined" color="success" size="sm" @click="saveAll" :disabled="isAllSaving">
          <span v-if="isAllSaving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          <span>{{ isAllSaving ? 'Saving...' : 'Save All' }}</span>
        </MaterialButton>
      </div> -->
    </template>
  </div>
</template>

<script setup>
import { reactive, watch, computed, ref } from "vue";
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialSelect from "@/components/common/MaterialSelect.vue";
import MaterialMultiSelect from "@/components/common/MaterialMultiSelect.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import MaterialSelectedBadge from "@/components/common/MaterialSelectedBadge.vue";
import MaterialSpinnerInput from "@/components/common/MaterialSpinnerInput.vue";

const props = defineProps({
  saveAllAllowed: { type: Boolean, default: true },
  initialRules: { type: Array, default: () => [] },
  actionOptions: {
    type: Array,
    default: () => [
      { value: "send", label: "Send" },
      { value: "do-not-send", label: "Do NOT Send" },
    ],
  },
  showSearch: { type: Boolean, default: false },
  savingRuleIds: { type: Array, default: () => [] },
  savingAll: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  skeletonCount: { type: Number, default: 3 },
  /** When true, rule number display starts from 0 (e.g. WorkgroupPreferences) */
  startRuleNumberFromZero: { type: Boolean, default: false },
});

const emit = defineEmits(["save", "saveAll", "info"]);

const rulesState = reactive(
  (props.initialRules || []).map((r) => ({
    ...r,
    actionOptions: r.actionOptions ? [...r.actionOptions] : null,
    inputFields: (r.inputFields || []).map((f) => ({
      ...f,
      modelValue: Array.isArray(f.modelValue) ? [...f.modelValue] : f.modelValue,
      options: Array.isArray(f.options) ? f.options.map(opt => ({ ...opt })) : f.options
    })),
  }))
);

// search demo state
const searchQuery = ref("");
const filteredRules = ref(["Test", "Test 2"]);

watch(
  () => props.initialRules,
  (next) => {
    rulesState.splice(
      0,
      rulesState.length,
      ...(next || []).map((r) => ({
        ...r,
        actionOptions: r.actionOptions ? [...r.actionOptions] : null,
        inputFields: (r.inputFields || []).map((f) => ({
          ...f,
          modelValue: Array.isArray(f.modelValue) ? [...f.modelValue] : f.modelValue,
          options: Array.isArray(f.options) ? f.options.map(opt => ({ ...opt })) : f.options
        })),
      }))
    );
  },
  { deep: true }
);

const isAllSaving = computed(() => !!props.savingAll);
function isRuleSaving(id) {
  return Array.isArray(props.savingRuleIds) && props.savingRuleIds.includes(id);
}

function removeFromResults(item) {
  const idx = filteredRules.value.indexOf(item);
  if (idx >= 0) filteredRules.value.splice(idx, 1);
}

function clearRuleFields(ruleId) {
  const rule = rulesState.find(r => r.id === ruleId);
  if (rule && rule.inputFields) {
    rule.inputFields.forEach(field => {
      if (field.type === 'multi') {
        field.modelValue = [];
      } else if (field.type === 'number') {
        field.modelValue = null;
      } else if (field.type === 'select') {
        field.modelValue = null;
      } else {
        field.modelValue = '';
      }
    });
  }
}

function clearAllRuleFields() {
  rulesState.forEach(rule => {
    if (rule.inputFields) {
      rule.inputFields.forEach(field => {
        if (field.type === 'multi') {
          field.modelValue = [];
        } else if (field.type === 'number') {
          field.modelValue = null;
        } else if (field.type === 'select') {
          field.modelValue = null;
        } else {
          field.modelValue = '';
        }
      });
    }
  });
}

function saveRule(rule) {
  const payload = { id: rule.id, action: rule.action, fields: {} };
  for (const f of rule.inputFields || []) {
    payload.fields[f.key ?? f.label] = f.modelValue;
  }
  emit("save", payload);
}

function saveAll() {
  const payload = rulesState.map((rule) => {
    const fields = {};
    for (const f of rule.inputFields || []) fields[f.key ?? f.label] = f.modelValue;
    return { id: rule.id, action: rule.action, fields };
  });
  emit("saveAll", payload);
}

// Spinner function for readonly numeric fields
function check_spinner(id, min, max) {
  // This function is called on click, but the input is readonly.
  // The actual value changes are handled by increment/decrement buttons in MaterialSpinnerInput.
  console.log(`Spinner for ${id} clicked. Min: ${min}, Max: ${max}`);
}

// Get CSS class for action select based on rule ID and action value
function getActionClass(ruleId, action) {
  // Only apply colors to rules 2 and 3
  if (ruleId === 2 || ruleId === 3) {
    return action === "send" ? "action-send" : "action-do-not-send";
  }
  return "";
}

// Get card class based on current action value
function getRuleCardClass(action) {
  if (action === 'never') return 'rule-card-never';
  if (action === 'when-possible') return 'rule-card-whenpossible';
  return '';
}

function getRuleNumberClass(action) {
  if (action === 'never') return 'rule-num-never';
  if (action === 'when-possible') return 'rule-num-whenpossible';
  return 'rule-num-default';
}

// Expose methods to parent component
defineExpose({
  clearRuleFields,
  clearAllRuleFields
});

</script>

<style scoped>
.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.skeleton-text {
  height: 16px;
  margin-bottom: 8px;
}

.skeleton-button {
  height: 32px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Style for Send action - Green text */
.action-send :deep(select),
.action-send :deep(select[id^="action-"]) {
  color: #28a745 !important; /* Bootstrap success green */
  font-weight: 500;
}

/* Style for Do NOT Send action - Red text */
.action-do-not-send :deep(select),
.action-do-not-send :deep(select[id^="action-"]) {
  color: #dc3545 !important; /* Bootstrap danger red */
  font-weight: 500;
}

/* Ensure dropdown options are always black */
.action-send :deep(select option),
.action-do-not-send :deep(select option) {
  color: #000000 !important; /* Black for all options */
  background-color: #ffffff !important;
}

/* ── Rule card action-type styling ── */
.rule-card {
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 12px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: visible;
}
.rule-card:hover {
  box-shadow: 0 3px 12px rgba(0,0,0,0.1);
}
.rule-card-never {
  border-left: 4px solid #e53935;
}
.rule-card-whenpossible {
  border-left: 4px solid #ff9800;
}

/* Rule card header */
.rule-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: #f8f9fb;
  border-bottom: 1px solid #eee;
  border-radius: 12px 12px 0 0;
}
.rule-card-body {
  padding: 0.75rem;
}
.rule-desc {
  color: #344767;
  font-weight: 500;
}
.rule-desc-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  line-height: 1.8;
}
.rule-desc-inline :deep(.input-group) {
  display: inline-flex;
  width: auto;
  vertical-align: middle;
}
.rule-desc-inline :deep(.input-group .btn) {
  padding: 0.15rem 0.3rem;
}
.rule-desc-inline :deep(.input-group .form-control) {
  width: 42px !important;
  min-width: 42px;
  padding: 0.15rem !important;
}
.rule-desc-inline :deep(.input-group select) {
  width: 130px !important;
  min-width: 130px;
  flex: 0 0 130px;
}
.rule-action-select {
  display: inline-flex;
  width: 130px;
  min-width: 130px;
  flex: 0 0 130px;
}

/* Rule number badge */
.rule-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.rule-num-default {
  background: #e8eaf6;
  color: #3949ab;
}
.rule-num-never {
  background: #fce4ec;
  color: #c62828;
}
.rule-num-whenpossible {
  background: #fff3e0;
  color: #e65100;
}

/* Info button */
.btn-rule-info {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #667eea;
  border-radius: 50%;
  transition: background 0.15s, color 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.btn-rule-info:hover {
  background: #eef0ff;
  color: #4c63d2;
}
.btn-rule-info i { font-size: 20px; }

/* Action hint subtitle */
.rule-action-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  margin-top: 6px;
  padding: 3px 10px;
  border-radius: 6px;
  width: fit-content;
}
.rule-hint-never {
  color: #c62828;
  background: #fce4ec;
}
.rule-hint-whenpossible {
  color: #e65100;
  background: #fff3e0;
}
</style>
